// ReferralsWrapper

import type React from "react";
import { useMemo } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { SWReactIcons } from "@similarweb/icons";
import { Tabs, TabList, Tab, TabPanel, NavItemBadge } from "@similarweb/ui-components";
import type { INavItemBadgeType } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import { openStateUnlockModal } from "app/services/ModalService";
import { isHidden, isLocked } from "app/common/services/pageClaims";
import { swSettings } from "app/common/services/swSettings";
import { allTrackers } from "app/services/track/track";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { FlexColumn } from "pro-features/styled components/StyledFlex/src/StyledFlex";

import { IncomingTrafficContainer } from "../../incoming-traffic/IncomingTrafficContainer";
import { OutgoingTrafficPage } from "../../outgoing-traffic/OutgoingTrafficPage";

import { ItemContainer } from "./styledComponent";

export enum ECategoryReferralsType {
    IncomingTraffic = "incomingTraffic",
    OutgoingTraffic = "outgoingTraffic",
}

interface TabConfig {
    id: ECategoryReferralsType;
    titleKey: string;
    Component: React.ComponentType;
    stateName: string;
    isLocked?: boolean;
    isHidden?: boolean;
    hook?: { name: string };
    badge?: INavItemBadgeType;
}

const getTabConfig = (): TabConfig[] => [
    {
        id: ECategoryReferralsType.IncomingTraffic,
        titleKey: "analysis.referrals.incoming.title",
        Component: IncomingTrafficContainer,
        stateName: "digitalsuite_website_referrals_incomingtraffic",
        isLocked: isLocked(swSettings.components.WebReferralIncomingTraffic),
        isHidden: isHidden(swSettings.components.WebReferralIncomingTraffic),
        hook: { name: "digitalsuite_website_referrals_incomingtraffic" },
    },

    {
        id: ECategoryReferralsType.OutgoingTraffic,
        titleKey: "analysis.referrals.outgoing.title",
        Component: OutgoingTrafficPage,
        stateName: "digitalsuite_website_referrals_outgoingtraffic",
        isLocked: isLocked(swSettings.components.WebReferralOutgoingTraffic),
        isHidden: isHidden(swSettings.components.WebReferralOutgoingTraffic),
        hook: { name: "digitalsuite_website_referrals_outgoingtraffic" },
    },
];

export const ReferralsWrapper: React.FC = () => {
    const { params } = useCurrentStateAndParams();
    const selectedTab = params.selectedTab as ECategoryReferralsType | undefined;
    const defaultTab = ECategoryReferralsType.IncomingTraffic;

    const visibleTabs = useMemo(() => getTabConfig().filter((tab) => !tab.isHidden), []);

    const activeTabId = selectedTab ?? defaultTab;

    const activeTabIndex = useMemo(() => {
        const index = visibleTabs.findIndex((tab) => tab.id === activeTabId);
        return index >= 0 ? index : 0;
    }, [activeTabId, visibleTabs]);

    const handleTabSelect = (index: number) => {
        const { id, isLocked, hook } = visibleTabs[index];
        allTrackers.dispatchTrackEvent("Button", "click", `Referrals Tab/${id}`);
        if (isLocked) {
            openStateUnlockModal(hook);
        } else {
            proNavigator.go("digitalsuite_website_referrals", { ...params, selectedTab: id, referralsCategory: "" });
        }
    };

    return (
        <Tabs
            selectedIndex={activeTabIndex}
            onSelect={handleTabSelect}
        >
            <FlexColumn gap="24px">
                <TabList>
                    {visibleTabs.map(({ id, titleKey, isLocked, badge }) => (
                        <Tab key={id}>
                            <ItemContainer>
                                <span>{translate(titleKey)}</span>
                                {isLocked ? (
                                    <SWReactIcons
                                        iconName="spark"
                                        size="xs"
                                    />
                                ) : null}
                                {badge ? <NavItemBadge type={badge} /> : null}
                            </ItemContainer>
                        </Tab>
                    ))}
                </TabList>
                {visibleTabs.map(({ Component }, index) => (
                    <TabPanel key={index}>{index === activeTabIndex ? <Component /> : null}</TabPanel>
                ))}
            </FlexColumn>
        </Tabs>
    );
};
